import Controller, { OnBeforeClose, OnBeforeFetch, OnChange, OnClick, OnMount, OnReadSuccess } from '@philips-internal/oswald-ui-core/lib/client';
import BaseController from '../../BaseController';
import { Inject } from '@odinjs/odin';
import { AbortableCloseEvent, FetchEvent, Mountable, MountEvent, ReadEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { ActionAPI, BarcodeAPI, BarcodeData, ContextualBarAPI, DeckAPI, FetchAPI, PanelAPI } from '@philips-internal/oswald-ui-components';
import { ChangeEvent } from '@philips-internal/oswald-ui-core/lib/component/types/changeable';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { ApplicationRoute } from '../../../constants/routes';
import { isFunction } from '@philips-internal/oswald-ui-utils';
import { GenerateTagDataParams } from '../generate-tag-data/generate-tag-data-params';

import { change } from '../../../interfaces/exam-collect/pending-exam-collection';
import { EC_9130Parameters } from '../../../utils/parameters';
import { FoundationUtils } from '../../../utils/foundation-utils';
import './pending-exam-collection-controller.css'
import {ExamCollectionWithBarcodeHandler} from './pending-exam-collection-handlers/exam-collection-with-barcode-handler';
import {ExamCollectionWithoutBarcodeHandler} from './pending-exam-collection-handlers/exam-collection-without-barcode-handler';
import { Value } from '@philips-internal/oswald-ui-core';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { FragmentNames, PendingExamCollectionAliases } from '../../../codegen';
import BarcodeService from '../../../services/barcode-service';

@Controller({ component: PendingExamCollectionAliases.PENDING_EXAM_COLLECTION_LAYOUT, fragment: FragmentNames.PENDING_EXAM_COLLECTION })
class PendingExamCollectionController extends BaseController {

  @Inject(PendingExamCollectionAliases.GENERATE_TAG_DATA_ACTION)
  private generateTagDataAction!: ActionAPI;

  @Inject(PendingExamCollectionAliases.TOAST_SUCCESSFULLY_PANEL)
  private toastSuccessfullyPanel!: PanelAPI;

  @Inject(PendingExamCollectionAliases.DECK_PENDING_EXAM_COLLECTION)
  private deckPendingExams!: DeckAPI;

  @Inject(PendingExamCollectionAliases.FETCH_PENDING_EXAM_COLLECTION)
  private fecthPendingExam!: FetchAPI;

  @Inject(PendingExamCollectionAliases.BARCODE_INPUT_FIELD)
  private barcodeApi!: BarcodeAPI;

  verifyTasyParameters(tasyParams: string): string {
    return FoundationUtils.findModuleParam('EXAM_COLLECT#9130', tasyParams) as string || FoundationUtils.findModuleParam('EXAM_COLLECT#9130', tasyParams) as string;
  }

  validateOptionIsVisible(): void {
    const paramUseLaboratorySampleStandard = this.verifyTasyParameters(EC_9130Parameters.USE_LABORATORY_SAMPLE_STANDARD) as string;
    const paramAllowsYouToGenerateTagDataWhenCollectingExam = this.verifyTasyParameters(EC_9130Parameters.ALLOWS_YOU_TO_GENERATE_TAG_DATA_WHEN_COLLECTING_EXAMS) as string;

    if (paramUseLaboratorySampleStandard !== 'N' || paramAllowsYouToGenerateTagDataWhenCollectingExam !== 'S') {
      this.generateTagDataAction.setEnabled(false);
    }
  }

  validateBarcode(event: ReadEvent<BarcodeData>): void {
    const {preventDefault} = event;
    if (isFunction(preventDefault)) {
      preventDefault();
    }
  }

  @OnMount(PendingExamCollectionAliases.PENDING_EXAM_COLLECTION_CONTEXTUAL_BAR)
  onContextualBarMount(evt: MountEvent): void {
    const contextualBar = evt.target as ContextualBarAPI;

    const { prescriptionId } = this.historyService.getCurrentRouteParams();

    const message = MessageService.translate('tws.pending-exam-collection.prescription-number');
    const title = message.replace('#@NR_PRESCRICAO#@', String(prescriptionId));

    contextualBar.setTitle(title);
  }

  @OnMount(PendingExamCollectionAliases.PENDING_EXAM_COLLECTION_LAYOUT)
  onMountLayout(): void {
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();

    if (GenerateTagDataParams.param?.generatedSuccessfully === 'S') {
      this.toastSuccessfullyPanel.setVisible(true);

      setTimeout(() => {
        this.toastSuccessfullyPanel.setVisible(false);
      }, 3000);
    }

    GenerateTagDataParams.param = { generatedSuccessfully: 'N' };

    const parameters = {
      paramAllowsYouToGenerateTagDataWhenCollectingExam: this.verifyTasyParameters(EC_9130Parameters.ALLOWS_YOU_TO_GENERATE_TAG_DATA_WHEN_COLLECTING_EXAMS) as string,
      paramExamStatusAfterCollection: this.verifyTasyParameters(EC_9130Parameters.EXAM_STATUS_AFTER_COLLECTION) as string,
      paramPreviousDateExams: this.verifyTasyParameters(EC_9130Parameters.PREVIOUS_DATE_EXAMS) as string,
      paramRequestPassword: this.verifyTasyParameters(EC_9130Parameters.REGISTER_COLLECTION_WITHOUT_THE_TAG) as string,
      paramUseLaboratorySampleStandard: this.verifyTasyParameters(EC_9130Parameters.USE_LABORATORY_SAMPLE_STANDARD) as string,
    }

    ExamCollectionWithoutBarcodeHandler.initialize(parameters);
    ExamCollectionWithBarcodeHandler.initialize(parameters);

    this.context.set({
      encounterId,
      patientId,
      ...parameters
    });
  }

  @OnMount(PendingExamCollectionAliases.PENDING_COLLECTION_EXAMS_CARD)
  onPendingExamsCardMount(evt: MountEvent): void {
    interface hasSetShowCheckbox extends Mountable {
      setShowCheckbox: (showCheckbox: boolean) => void;
    }
    const card = evt.target as hasSetShowCheckbox;

    const isCardSelectable = ExamCollectionWithoutBarcodeHandler.userCanSubmitExamSubsetList();
    card.setShowCheckbox(isCardSelectable);
  }

  @OnMount(PendingExamCollectionAliases.GENERATE_TAG_DATA_ACTION)
  onMountGenerateTagDataAction(): void {
    this.validateOptionIsVisible();
  }

  @OnBeforeFetch(PendingExamCollectionAliases.FETCH_PENDING_EXAM_COLLECTION)
  onBeforeFetchPhysician(evt: FetchEvent): void {
    if (evt) {
      const { encounterId, prescriptionId } = this.historyService.getCurrentRouteParams();
      const params = {
        encounterNumber: encounterId,
        prescriptionNumber: prescriptionId,
        fullList: true,
      } as Record<string, Value>;
      this.fecthPendingExam.setParameters(params);
      this.context.set({ beepedBarcodes: [] });
    }
  }

  @OnChange(PendingExamCollectionAliases.PENDING_COLLECTION_EXAMS_CARD)
  onSelectExamWithoutBarcode(evt: ChangeEvent<change>): void {
    const enabled: boolean = evt.value.checkbox;
    const card = evt.value.card;

    ExamCollectionWithoutBarcodeHandler.selectExam(enabled, card);
  }

  @OnClick(PendingExamCollectionAliases.MENU_PENDING_EXAM_COLLECTION_1)
  onClickUnlabelledCollection(): void {
    ExamCollectionWithoutBarcodeHandler.submitToCollection(this.fecthPendingExam);
  }

  @OnClick(PendingExamCollectionAliases.GENERATE_TAG_DATA_ACTION)
  async onClickGenerateTagDataMenuAndRedirect(): Promise<void> {
    const { patientId, encounterId, prescriptionId } = this.historyService.getCurrentRouteParams();
    const redirectRoute = `${ApplicationRoute.EXAM_COLLECTION}${ApplicationRoute.EXAM_COLLECT_GENERATE_TAG_DATA}`;

    return applicationContext.redirect(redirectRoute, {
      encounterId,
      patientId,
      prescriptionId,
    });
  }

  @OnBeforeClose(PendingExamCollectionAliases.BARCODE_INPUT_FIELD)
  onBeforeBarcodeClose(evt: AbortableCloseEvent): void {
    ExamCollectionWithBarcodeHandler.onExitBarcode(evt, this.barcodeApi, this.deckPendingExams, this.fecthPendingExam);
  }

  @OnMount(PendingExamCollectionAliases.BARCODE_INPUT_FIELD)
  handleMountBarcode(event: MountEvent): void {
    const barcodeApi = event.target as BarcodeAPI
    barcodeApi.setDefaultReadType(BarcodeService.getDefaultMethodOfReadingBarcode())
  }

  @OnReadSuccess(PendingExamCollectionAliases.BARCODE_INPUT_FIELD)
  async onBarcodeReadSuccess(event: ReadEvent<BarcodeData>): Promise<void> {
    const {preventDefault} = event;
    if (isFunction(preventDefault)) {
      preventDefault();
    }

    ExamCollectionWithBarcodeHandler.onBarcodeReadSuccess(event.item.value.trim(), this.barcodeApi, this.deckPendingExams, this.fecthPendingExam);
  }
}

export default PendingExamCollectionController;
